import React, { useState, useEffect, useCallback } from "react";
import { Play, FileText, Image as ImageIcon, Video, FileAudio, Download, Loader2, AlertCircle } from "lucide-react";
import { listQuoteAttachments, getViewUrl, humanSize } from "../../lib/attachments";

const iconFor = (c) => c === "image" ? ImageIcon : c === "video" ? Video : c === "audio" ? FileAudio : FileText;
const fmtTime = (s) => s == null ? "" : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

/**
 * Secure attachment viewer. Fetches the linked attachment list for a quote
 * (backend authorises owner/matched-provider/admin) and lazily requests a
 * short-lived signed URL only when the viewer opens a specific item.
 */
export const AttachmentViewer = ({ quoteId, accessToken, title = "Customer Photos, Videos, Voice Notes & Files" }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [thumbs, setThumbs] = useState({}); // id -> signed thumb url
  const [open, setOpen] = useState(null);   // { url, mime, category }
  const [err, setErr] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    if (!accessToken) { setLoading(false); return; }   // wait for auth token
    (async () => {
      setLoading(true); setErr("");
      try {
        const rows = await listQuoteAttachments(quoteId, accessToken);
        if (alive) setItems(rows);
      } catch (e) {
        if (alive) { setItems([]); setErr("Couldn't load attachments."); }
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [quoteId, accessToken, reloadKey]);

  // Lazily load thumbnails for images/videos that have one
  useEffect(() => {
    let alive = true;
    (async () => {
      for (const it of items) {
        if ((it.category === "image" || (it.category === "video" && it.has_thumbnail)) && !thumbs[it.id]) {
          try {
            const { url } = await getViewUrl(it.id, accessToken, { thumbnail: it.category === "video" || it.has_thumbnail });
            if (alive) setThumbs(prev => ({ ...prev, [it.id]: url }));
          } catch { /* ignore individual thumb errors */ }
        }
      }
    })();
    return () => { alive = false; };
  }, [items, accessToken, thumbs]);

  const openItem = useCallback(async (it) => {
    setErr("");
    try {
      const data = await getViewUrl(it.id, accessToken);
      if (it.category === "document") { window.open(data.url, "_blank", "noopener,noreferrer"); return; }
      setOpen({ ...data, name: it.original_file_name });
    } catch (e) { setErr(e.message || "Could not open attachment."); }
  }, [accessToken]);

  if (loading) return <div data-testid="attach-viewer-loading" style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}><Loader2 size={14} className="spin" /> Loading attachments…</div>;
  if (err && !items.length) return (
    <div data-testid="attach-viewer-error" style={{ fontSize: "0.8125rem", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 8 }}>
      <AlertCircle size={14} color="#dc2626" /> {err}
      <button type="button" data-testid="attach-viewer-retry" onClick={() => setReloadKey(k => k + 1)} className="btn-outline" style={{ fontSize: "0.75rem", padding: "3px 8px" }}>Retry</button>
    </div>
  );
  if (!items.length) return null;

  return (
    <div data-testid="attachment-viewer" style={{ marginTop: "16px" }}>
      <h3 style={{ fontSize: "0.9375rem", fontWeight: 700, marginBottom: "10px", color: "var(--text-dark)" }}>{title}</h3>
      {err && <p style={{ color: "#dc2626", fontSize: "0.8125rem" }}><AlertCircle size={13} style={{ verticalAlign: "middle" }} /> {err}</p>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "10px" }}>
        {items.map(it => {
          const Icon = iconFor(it.category);
          const thumb = thumbs[it.id];
          return (
            <button key={it.id} type="button" data-testid={`view-attach-${it.id}`} onClick={() => openItem(it)}
              className="attach-view-tile"
              style={{ textAlign: "left", border: "1px solid var(--border-color)", borderRadius: "var(--radius-sm)", overflow: "hidden", background: "var(--card-bg)", cursor: "pointer", padding: 0 }}
              aria-label={`Open ${it.category} ${it.original_file_name}`}>
              <div style={{ height: "92px", background: "#0f172a10", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                {thumb ? <img src={thumb} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <Icon size={24} color="var(--text-muted)" />}
                {it.category === "video" && <Play size={22} color="#fff" style={{ position: "absolute", filter: "drop-shadow(0 1px 2px #000)" }} />}
              </div>
              <div style={{ padding: "7px" }}>
                <div style={{ fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={it.original_file_name}>
                  {it.category === "audio" && it.original_file_name?.startsWith("voice-note") ? "Voice note" : it.original_file_name}
                </div>
                <div style={{ fontSize: "0.66rem", color: "var(--text-muted)" }}>{humanSize(it.file_size)}{it.duration_seconds != null ? ` · ${fmtTime(it.duration_seconds)}` : ""}</div>
              </div>
            </button>
          );
        })}
      </div>

      {open && (
        <div role="dialog" aria-label="Attachment preview" data-testid="attach-lightbox"
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "20px" }}
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(null); }}>
          <div style={{ maxWidth: "900px", width: "100%", textAlign: "center" }}>
            {open.category === "image" && <img src={open.url} alt={open.name} style={{ maxWidth: "100%", maxHeight: "80vh", borderRadius: "8px" }} />}
            {open.category === "video" && <video src={open.url} controls autoPlay={false} style={{ maxWidth: "100%", maxHeight: "80vh", borderRadius: "8px" }} />}
            {open.category === "audio" && <audio src={open.url} controls autoPlay={false} style={{ width: "100%" }} />}
            <div style={{ marginTop: "12px", display: "flex", gap: "8px", justifyContent: "center" }}>
              <a href={open.url} target="_blank" rel="noopener noreferrer" download className="btn-outline" data-testid="attach-download" style={{ display: "inline-flex", gap: "6px", alignItems: "center" }}><Download size={14} /> Download</a>
              <button type="button" className="btn-primary" data-testid="attach-close-preview" onClick={() => setOpen(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttachmentViewer;
